import React from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import srcIcon from 'assets/images/User-Question.png';
import StyledAccordion from '../../../_shared/accordion';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSurveyContent } from '../../../../shared/hooks';
import titleCase from '../../../../shared/helpers/titleCase';
import { useHandleSubmit, useRedirectToUrl } from '../PaymentClaim/hooks';
import { surveyPortalMixpanel } from '../../../../vendor/mixpanel';

interface FormInputs {
  email: string;
  confirmEmail: string;
}

const Screened: React.FC = () => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormInputs>();
  const { surveyContent } = useSurveyContent();
  const emailValue = watch('email');
  const seid = surveyContent?.subject ?? '';
  const screener_comp = surveyContent?.content?.screener_compensation ?? 0;
  const postPayment = useHandleSubmit();
  const urlAux = window.location.href.split('/s/');
  const basePaymentPendingUrl = urlAux[0] + '/payment_pending';
  const paymentPendingUrl = new URL(basePaymentPendingUrl);
  paymentPendingUrl.searchParams.append('survey_template', 'SURVEY_2025');
  paymentPendingUrl.searchParams.append('seid', seid);
  paymentPendingUrl.searchParams.append('email', emailValue);
  useRedirectToUrl(paymentPendingUrl.toString());
  const userName = surveyContent?.subject_given_name ?? '';
  const reasons = surveyContent?.content?.disqualification_reasons ?? [];
  const source = surveyContent?.survey_host ?? '';
  const environment = process.env.REACT_APP_ENV;
  const survey_id = surveyContent?.survey_id;
  let suffix = '';
  if (environment === 'local' || environment === 'development') {
    suffix = '.dev';
  } else if (environment === 'staging') {
    suffix = '.staging';
  }
  // UTM Sign-up URL
  const signUpUrl = `https://professionals${suffix}.newtonx.com/?utm_source=${source}&utm_medium=survey-experience&utm_campaign=survey-${seid}&utm_content=screened-page&utm_term=survey_id_${survey_id}`;
  const hasTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedRef.current && surveyContent?.subject) {
      if (screener_comp && screener_comp > 0) {
        surveyPortalMixpanel.track('Screen out page with comp Viewed', {
          SSEID: surveyContent?.subject,
          survey_id: surveyContent?.survey_id,
          survey_host: surveyContent?.survey_host,
        });
      } else {
        surveyPortalMixpanel.track('Screen Out Page Viewed', {
          SSEID: surveyContent?.subject,
          survey_id: surveyContent?.survey_id,
          survey_host: surveyContent?.survey_host,
        });
      }
      hasTrackedRef.current = true;
    }
  }, [surveyContent]);
  const trackUpdateProfile = () => {
    surveyPortalMixpanel.track('Update Profile Button Clicked', {
      SSEID: surveyContent?.subject,
      survey_id: surveyContent?.survey_id,
      survey_host: surveyContent?.survey_host,
      source: 'Survey',
      button_location: 'Screened out page',
    });
  };
  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const formattedData = {
      request_url: window.location.href,
      email: data.email,
    };
    postPayment(formattedData);
  };
  const accordionItems = [
    {
      title: 'Why didn’t I qualify for this survey?',
      content: (
        <>
          <Typography variant="body2" mb="8px">
            Reasons you may not have qualified for this survey include:
          </Typography>
          <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
            {reasons.map((r, i) => (
              <li key={i}>
                <Typography variant="body2">{r}</Typography>
              </li>
            ))}
          </ul>
        </>
      ),
    },
    {
      title: 'Am I going to get paid?',
      content: (
        <Typography variant="body2">
          {screener_comp && screener_comp > 0
            ? `Yes, as a thank you for taking part you will be paid $${screener_comp}. Please let us know
          where to send your payment by providing your email address in the fields at the top of
          this page`
            : `Because you did not advance beyond the screener, you are not eligible for payment`}
          .
        </Typography>
      ),
    },
    {
      title: 'I don’t see my question answered here.',
      content: (
        <Typography variant="body2">
          For any additional questions or issues, get in touch with us via
          professionals@newtonx.com.
        </Typography>
      ),
    },
  ];
  return (
    <Box maxWidth="480px" component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h2" fontWeight={700} marginBottom="16px">
        {userName && userName.length > 1
          ? titleCase(userName) + ', this survey isn’t quite a fit'
          : 'This survey isn’t quite a fit'}
      </Typography>
      {screener_comp && screener_comp > 0 ? (
        <>
          <Typography variant="body1" color="text.secondary" mb="16px">
            We know this isn’t the outcome you were hoping for.
          </Typography>

          <Typography variant="body1" color="text.secondary" mb="16px">
            We’d like to send you a ${screener_comp} payment to thank you for taking part. Let us
            know where to email your payment.
          </Typography>

          {/* Email */}
          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Required field',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // basic email regex
                message: 'Please enter a valid email address',
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label="Email address*"
                sx={{ marginBottom: '16px' }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          {/* Confirm Email */}
          <Controller
            name="confirmEmail"
            control={control}
            rules={{
              required: 'Required field',
              validate: (value) => value === emailValue || 'Email addresses must match',
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label="Confirm email address*"
                sx={{ marginBottom: '16px' }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Button fullWidth variant="contained" color="primary" type="submit">
            SUBMIT
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body1" color="text.secondary" mb="32px">
            We know this isn’t the outcome you were hoping for. We hope we’ll find an opportunity to
            work with you soon.
          </Typography>

          <Typography variant="h3" fontWeight={700} marginBottom="16px">
            Unlock more opportunities!
          </Typography>

          <Typography variant="body1" color="text.secondary" mb="16px">
            Your latest experience helps us find surveys tailored to you. Take a moment to update
            your NewtonX Professionals Community profile—or sign up if you don’t yet have one.
          </Typography>

          <Button
            variant="outlined"
            sx={{ mb: '0px' }}
            fullWidth
            href={signUpUrl}
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackUpdateProfile}
          >
            UPDATE PROFILE
          </Button>
        </>
      )}

      <Box display="flex" marginBottom="8px" marginTop="24px">
        <Box
          component={'img'}
          sx={{
            width: '24px',
            height: '24px',
            marginRight: '8px',
          }}
          alt="user_questions"
          src={srcIcon}
        ></Box>
        <Typography variant="h4" fontWeight={700}>
          Frequently Asked Questions
        </Typography>
      </Box>

      <Box>
        <StyledAccordion items={accordionItems}></StyledAccordion>
      </Box>
    </Box>
  );
};

export default Screened;
